import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import Button from "@/shared/components/Button";
import LinearLayout from "@/shared/components/LinearLayout";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Textarea } from "@/shared/components/ui/textarea";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/shared/components/ui/drawer";
import { Info, X } from "lucide-react";
import { useSetupKyc } from "../services";
import type { KycSetupRequest } from "../interfaces/kyc.interface";
import {
  DOCUMENT_TYPES,
  INCOME_SOURCES,
  OCCUPATIONS,
  OPEN_ACCOUNT_REASONS,
} from "../interfaces/kyc.interface";
import { isApiError } from "@/shared/api";
import { reportToSentry } from "@/shared/monitoring/sentry";
import CountryCodeDrawer from "@/features/profile/components/CountryCodeDrawer";
import { countryCodes } from "@/features/profile/constants/country-codes";
import { p100SupportedCountryOptions } from "../constants/supported-countries";
import { setupKycSchema, type SetupKycFormValues } from "../schemas/setup-kyc";

const formatLabel = (value: string) =>
  value
    .replace(/_/g, " ")
    .replace(/\b([a-z])/gi, (match) => match.toUpperCase());

const documentTypeOptions = DOCUMENT_TYPES.map((value) => ({
  label: formatLabel(value),
  value,
}));

const incomeSourceOptions = INCOME_SOURCES.map((value) => ({
  label: formatLabel(value),
  value,
}));

const accountReasonOptions = OPEN_ACCOUNT_REASONS.map((value) => ({
  label: formatLabel(value),
  value,
}));

const countryOptions = p100SupportedCountryOptions;

const occupationOptions = OCCUPATIONS.map((value) => ({
  label: formatLabel(value),
  value,
}));

const yesNoOptions = [
  { label: "Yes", value: "YES" },
  { label: "No", value: "NO" },
] as const;

const ADDITIONAL_INFO_REQUIRED_INCOME_SOURCES = new Set([
  "OTHER",
  "MAINTAINED_BY_FAMILY",
  "INVESTMENTS",
  "SOCIAL_BENEFITS",
  "TENANCY",
]);

const KycForm = () => {
  const navigate = useNavigate();
  const { mutateAsync: triggerSetupKyc, isPending: isSubmitting } =
    useSetupKyc();

  const [isCountryDrawerOpen, setIsCountryDrawerOpen] = useState(false);
  const [selectedCountryIso, setSelectedCountryIso] = useState("US");

  const form = useForm<SetupKycFormValues>({
    resolver: zodResolver(setupKycSchema),
    defaultValues: {
      documentType: DOCUMENT_TYPES[0],
      country: "US",
      incomeSource: INCOME_SOURCES[0],
      openAccountReason: OPEN_ACCOUNT_REASONS[0],
      phone: "",
      street: "",
      number: "",
      city: "",
      postCode: "",
      citizenship: "US",
      nationality: "US",
      birthCountry: "US",
      birthDate: "",
      occupation: "",
      taxId: "",
      additionalInfo: "",
      isPep: false,
      isFatca: false,
    },
    mode: "onChange",
  });
  const incomeSource = form.watch("incomeSource");
  const showOccupationField = incomeSource === "SALARY";
  const showTaxIdField = incomeSource === "BUSINESS_ACTIVITY";
  const showAdditionalInfoField =
    typeof incomeSource === "string" &&
    ADDITIONAL_INFO_REQUIRED_INCOME_SOURCES.has(incomeSource);

  useEffect(() => {
    if (!showOccupationField && form.getValues("occupation")) {
      form.setValue("occupation", "", {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.clearErrors("occupation");
    }

    if (!showTaxIdField && form.getValues("taxId")) {
      form.setValue("taxId", "", {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.clearErrors("taxId");
    }

    if (!showAdditionalInfoField && form.getValues("additionalInfo")) {
      form.setValue("additionalInfo", "", {
        shouldDirty: true,
        shouldValidate: true,
      });
      form.clearErrors("additionalInfo");
    }
  }, [form, showAdditionalInfoField, showOccupationField, showTaxIdField]);

  const renderInfoDrawer = (
    title: string,
    description: string,
    ariaLabel: string
  ) => (
    <Drawer direction="bottom">
      <DrawerTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className="inline-flex items-center justify-center rounded-full p-1 text-white/60 transition hover:bg-white/10 hover:text-white">
          <Info className="size-4" />
        </button>
      </DrawerTrigger>
      <DrawerContent className="border-t border-border bg-sidebar-active text-white">
        <DrawerHeader className="border-b border-border text-left">
          <div className="flex items-center justify-between gap-2">
            <DrawerTitle className="text-base font-semibold">
              {title}
            </DrawerTitle>
            <DrawerClose asChild>
              <button
                type="button"
                className="flex h-8 w-8 items-center justify-center rounded-full text-white/70 transition hover:bg-white/10 hover:text-white">
                <X className="size-4" />
              </button>
            </DrawerClose>
          </div>
        </DrawerHeader>
        <div className="space-y-3 p-4 text-sm text-white/80">
          <p>{description}</p>
        </div>
      </DrawerContent>
    </Drawer>
  );

  const renderSelect = (
    name: Exclude<keyof SetupKycFormValues, "isPep" | "isFatca">,
    label: string,
    options: { label: string; value: string }[]
  ) => (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <Select value={field.value} onValueChange={field.onChange}>
            <FormControl>
              <SelectTrigger className="w-full">
                <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
              </SelectTrigger>
            </FormControl>
            <SelectContent className="w-full">
              <SelectGroup>
                <SelectLabel>{label}</SelectLabel>
                {options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const parsedValues = setupKycSchema.parse(values);
      const sanitizedPhone = parsedValues.phone.replace(/\+/g, "");
      const payload = Object.fromEntries(
        Object.entries({
          ...parsedValues,
          phone: sanitizedPhone,
        }).filter(([, value]) => value !== undefined)
      ) as KycSetupRequest;

      const response = await triggerSetupKyc(payload);

      toast.success("Redirecting to verification...");

      setTimeout(() => {
        if (response.redirectUrl) {
          window.location.href = response.redirectUrl;
          return;
        }

        navigate("/kyc-verification-success");
      }, 500);
    } catch (error) {
      if (isApiError(error)) {
        toast.error(
          error.message ??
            "Unable to start verification. Please check your details and try again."
        );
        if (error.errors) {
          for (const [field, fieldError] of Object.entries(error.errors)) {
            const fieldName = field as keyof SetupKycFormValues & string;

            form.setError(fieldName, {
              type: "server",
              message: Array.isArray(fieldError)
                ? fieldError.join(" ")
                : String(fieldError),
            });
          }
        }
      } else {
        reportToSentry(error, {
          mechanism: "app",
          handled: true,
          tags: {
            feature: "kyc",
          },
        });
        toast.error("Unexpected error. Please try again later.");
      }
    }
  });

  const disableSubmit = useMemo(
    () => isSubmitting || !form.formState.isValid,
    [form.formState.isValid, isSubmitting]
  );

  const phoneValue = form.watch("phone");

  const selectedCountry = useMemo(() => {
    return (
      countryCodes.find((country) => country.code === selectedCountryIso) ??
      countryCodes.find((country) => country.code === "US") ??
      countryCodes[0]
    );
  }, [selectedCountryIso]);

  const matchedCountry = useMemo(() => {
    if (!phoneValue) {
      return null;
    }

    return countryCodes.find((country) =>
      phoneValue.startsWith(country.dialCode)
    );
  }, [phoneValue]);

  useEffect(() => {
    if (matchedCountry && matchedCountry.code !== selectedCountryIso) {
      setSelectedCountryIso(matchedCountry.code);
    }
  }, [matchedCountry, selectedCountryIso]);

  const localPhoneValue = useMemo(() => {
    if (!phoneValue) {
      return "";
    }

    const activeCountry = matchedCountry ?? selectedCountry;

    if (!activeCountry) {
      return phoneValue;
    }

    const dialCode = activeCountry.dialCode;
    if (phoneValue.startsWith(dialCode)) {
      return phoneValue.slice(dialCode.length);
    }

    return phoneValue;
  }, [matchedCountry, phoneValue, selectedCountry]);

  return (
    <div className="w-full">
      <LinearLayout orientation="vertical">
        <div className="space-y-6 text-white">
          <div className="space-y-2 text-center">
            <p className="text-xl font-medium">Verify Your Identity</p>
            <p className="font-inter text-sm text-white/70">
              Complete the form below so we can redirect you to our trusted KYC
              provider.
            </p>
          </div>

          <Form {...form}>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {renderSelect(
                  "documentType",
                  "Document Type",
                  documentTypeOptions
                )}
                {renderSelect("country", "Country", countryOptions)}
                {renderSelect(
                  "incomeSource",
                  "Income Source",
                  incomeSourceOptions
                )}
                {renderSelect(
                  "openAccountReason",
                  "Account Opening Reason",
                  accountReasonOptions
                )}
                <FormField
                  control={form.control}
                  name="isPep"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center justify-between gap-2">
                        <span>
                          PEP Declaration
                          <span className="block text-xs font-normal text-white/60">
                            Are you a Politically Exposed Person (PEP) or part
                            of a PEP group?
                          </span>
                        </span>
                        {renderInfoDrawer(
                          "PEP Declaration",
                          "A Politically Exposed Person (PEP) is someone who holds (or has held) a prominent public function, or is closely related/associated with such a person.",
                          "PEP declaration info"
                        )}
                      </FormLabel>
                      <Select
                        value={field.value ? "YES" : "NO"}
                        onValueChange={(value) =>
                          field.onChange(value === "YES")
                        }>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select an option" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="w-full">
                          <SelectGroup>
                            <SelectLabel>PEP Declaration</SelectLabel>
                            {yesNoOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="isFatca"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center justify-between gap-2">
                        <span>
                          FATCA Declaration
                          <span className="block text-xs font-normal text-white/60">
                            Are you a U.S. tax reportable person (FATCA)?
                          </span>
                        </span>
                        {renderInfoDrawer(
                          "FATCA Declaration",
                          "FATCA may apply if you are considered a U.S. tax reportable person (for example, a U.S. citizen, U.S. resident, or meet other U.S. tax reporting criteria).",
                          "FATCA declaration info"
                        )}
                      </FormLabel>
                      <Select
                        value={field.value ? "YES" : "NO"}
                        onValueChange={(value) =>
                          field.onChange(value === "YES")
                        }>
                        <FormControl>
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select an option" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="w-full">
                          <SelectGroup>
                            <SelectLabel>FATCA Declaration</SelectLabel>
                            {yesNoOptions.map((option) => (
                              <SelectItem
                                key={option.value}
                                value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone Number</FormLabel>
                      <div className="flex overflow-hidden rounded-lg border border-input bg-main-dark text-white">
                        <Drawer
                          direction="right"
                          open={isCountryDrawerOpen}
                          onOpenChange={setIsCountryDrawerOpen}>
                          <DrawerTrigger asChild>
                            <button
                              type="button"
                              aria-label="Select country code"
                              className="flex items-center gap-2 px-3 py-2 text-left text-sm outline-none transition hover:bg-white/5 focus-visible:bg-white/10 focus-visible:ring-2 focus-visible:ring-ring/50">
                              <span className="text-lg leading-none">
                                {selectedCountry.flag}
                              </span>
                              <span className="text-sm font-semibold text-white/90">
                                {selectedCountry.dialCode}
                              </span>
                            </button>
                          </DrawerTrigger>
                          <DrawerContent className="w-full border-l border-border bg-sidebar-active text-white sm:max-w-sm">
                            <DrawerHeader className="border-b border-border text-left">
                              <div className="flex items-center justify-between gap-2">
                                <DrawerTitle className="text-base font-semibold">
                                  Select Country Code
                                </DrawerTitle>
                                <DrawerClose asChild>
                                  <button
                                    type="button"
                                    className="flex h-8 w-8 items-center justify-center rounded-full text-white/70 transition hover:bg-white/10 hover:text-white">
                                    <X className="size-4" />
                                  </button>
                                </DrawerClose>
                              </div>
                            </DrawerHeader>
                            <div className="h-screen overflow-hidden px-4 pb-6 pt-4">
                              {isCountryDrawerOpen && (
                                <CountryCodeDrawer
                                  selectedDialCode={selectedCountry.dialCode}
                                  selectedIso={selectedCountry.code}
                                  onSelect={(country) => {
                                    setSelectedCountryIso(country.code);
                                    form.setValue(
                                      "phone",
                                      `${country.dialCode}${localPhoneValue}`.replace(
                                        /\s+/g,
                                        ""
                                      ),
                                      {
                                        shouldDirty: true,
                                        shouldValidate: true,
                                      }
                                    );
                                    setIsCountryDrawerOpen(false);
                                  }}
                                />
                              )}
                            </div>
                          </DrawerContent>
                        </Drawer>
                        <FormControl>
                          <Input
                            ref={field.ref}
                            type="tel"
                            autoComplete="tel"
                            value={localPhoneValue}
                            onChange={(event) => {
                              const rawValue = event.target.value.replace(
                                /[^0-9]/g,
                                ""
                              );
                              form.setValue(
                                "phone",
                                `${selectedCountry.dialCode}${rawValue}`,
                                {
                                  shouldValidate: true,
                                  shouldDirty: true,
                                }
                              );
                            }}
                            onBlur={field.onBlur}
                            placeholder="1234567890"
                            className="flex-1 border-0 bg-transparent text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
                          />
                        </FormControl>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="birthDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Birth Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="street"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Street</FormLabel>
                      <FormControl>
                        <Input placeholder="Main St" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Number</FormLabel>
                      <FormControl>
                        <Input placeholder="4A" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>City</FormLabel>
                      <FormControl>
                        <Input placeholder="New York" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="postCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Post Code</FormLabel>
                      <FormControl>
                        <Input placeholder="10001" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {renderSelect("citizenship", "Citizenship", countryOptions)}
                {renderSelect("nationality", "Nationality", countryOptions)}
                {renderSelect("birthCountry", "Birth Country", countryOptions)}
                {showOccupationField
                  ? renderSelect("occupation", "Occupation", occupationOptions)
                  : null}
                {showTaxIdField ? (
                  <FormField
                    control={form.control}
                    name="taxId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tax ID</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Enter tax ID"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : null}
                {showAdditionalInfoField ? (
                  <FormField
                    control={form.control}
                    name="additionalInfo"
                    render={({ field }) => (
                      <FormItem className="md:col-span-2">
                        <FormLabel>Additional Info</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Describe your income source"
                            className="min-h-24"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : null}
              </div>

              <Button
                type="submit"
                variant="primary"
                fluid
                loading={isSubmitting}
                disabled={disableSubmit}>
                {isSubmitting
                  ? "Starting Verification..."
                  : "Start Verification"}
              </Button>
            </form>
          </Form>
        </div>
      </LinearLayout>
    </div>
  );
};

export default KycForm;
